import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type CommsActivityBucketSize,
  type CommsExcerptCreate,
  type CommsMessagesPage,
  type CommsNotificationsPage,
  type CommsRange,
  type CommsWebVisitsPage,
  commsApi,
} from '@/services/api/comms'

// Sealed parquet evidence is immutable — once fetched for an extraction id it
// can never go stale, so nothing here ever background-refetches.
const IMMUTABLE = Number.POSITIVE_INFINITY

/** Chat pages this size: fewer full-file scans per thread (router caps at 200). */
const PAGE_SIZE = 200

export const commsKeys = {
  extractions: (caseId: string) => ['comms', 'extractions', caseId] as const,
  conversations: (caseId: string, extractionId: string, range: CommsRange) =>
    ['comms', 'conversations', caseId, extractionId, range.from ?? '', range.to ?? ''] as const,
  messages: (
    caseId: string,
    extractionId: string,
    conversationId: string,
    anchor: string | null,
    range: CommsRange
  ) =>
    [
      'comms',
      'messages',
      caseId,
      extractionId,
      conversationId,
      anchor,
      range.from ?? '',
      range.to ?? '',
    ] as const,
  activity: (caseId: string, extractionId: string, conversationId: string, bucket: string) =>
    ['comms', 'activity', caseId, extractionId, conversationId, bucket] as const,
  webVisits: (caseId: string, extractionId: string, range: CommsRange) =>
    ['comms', 'web-visits', caseId, extractionId, range.from ?? '', range.to ?? ''] as const,
  notifications: (caseId: string, extractionId: string, range: CommsRange) =>
    ['comms', 'notifications', caseId, extractionId, range.from ?? '', range.to ?? ''] as const,
  messageSearch: (
    caseId: string,
    extractionId: string,
    q: string,
    fuzzy: boolean,
    range: CommsRange
  ) =>
    [
      'comms',
      'message-search',
      caseId,
      extractionId,
      q,
      fuzzy,
      range.from ?? '',
      range.to ?? '',
    ] as const,
  excerpts: (caseId: string, extractionId: string, conversationId: string) =>
    ['comms', 'excerpts', caseId, extractionId, conversationId] as const,
}

export function useCommsExtractions(caseId: string) {
  return useQuery({
    queryKey: commsKeys.extractions(caseId),
    queryFn: () => commsApi.listExtractions(caseId),
    enabled: !!caseId,
  })
}

export function useCommsConversations(
  caseId: string,
  extractionId: string | null,
  range: CommsRange = {}
) {
  return useQuery({
    queryKey: commsKeys.conversations(caseId, extractionId ?? '', range),
    queryFn: () => commsApi.listConversations(caseId, extractionId as string, range),
    enabled: !!caseId && !!extractionId,
    staleTime: IMMUTABLE,
  })
}

/**
 * Chat-style paging: the first page is the newest slice of the thread; older
 * pages are fetched with `before = oldest sentAt seen` as the user scrolls up.
 *
 * `anchor` opens the thread at a specific message instead of at its end — what
 * a search hit needs. The first page then *ends* at that message, so the
 * thread's scroll-to-bottom lands on it with its history above; without this a
 * hit from 2019 would open a thread showing last week.
 */
export function useCommsMessages(
  caseId: string,
  extractionId: string | null,
  conversationId: string | null,
  anchor: string | null = null,
  range: CommsRange = {}
) {
  return useInfiniteQuery({
    queryKey: commsKeys.messages(caseId, extractionId ?? '', conversationId ?? '', anchor, range),
    queryFn: ({ pageParam }) =>
      commsApi.listMessages(caseId, extractionId as string, conversationId as string, {
        before: pageParam ?? undefined,
        limit: PAGE_SIZE,
        ...range,
      }),
    // `before` is exclusive, so the anchor is nudged a millisecond forward to
    // keep the anchored message itself inside the page.
    initialPageParam: (anchor ? new Date(new Date(anchor).getTime() + 1).toISOString() : null) as
      | string
      | null,
    getNextPageParam: (last: CommsMessagesPage) =>
      last.hasMore ? (last.messages[0]?.sentAt ?? null) : null,
    enabled: !!caseId && !!extractionId && !!conversationId,
    staleTime: IMMUTABLE,
  })
}

/** Shortest query worth a round-trip; mirrors the router's `min_length`. */
export const MESSAGE_SEARCH_MIN = 2

/**
 * Full-text hits across every thread of the extraction.
 *
 * Debouncing belongs to the caller — this fires per distinct query string and
 * caches each one forever, because the parquet behind it cannot change.
 */
export function useCommsMessageSearch(
  caseId: string,
  extractionId: string | null,
  query: string,
  fuzzy: boolean,
  range: CommsRange = {}
) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: commsKeys.messageSearch(caseId, extractionId ?? '', trimmed, fuzzy, range),
    queryFn: () =>
      commsApi.searchMessages(caseId, extractionId as string, trimmed, { fuzzy, ...range }),
    enabled: !!caseId && !!extractionId && trimmed.length >= MESSAGE_SEARCH_MIN,
    staleTime: IMMUTABLE,
  })
}

/**
 * Browsing history, newest first, paged by offset as the user scrolls. Offset
 * paging is safe here precisely because the parquet is immutable — pages never
 * shift — and it sidesteps the tie/NULL pitfalls a timestamp cursor has on the
 * non-unique, nullable `visitedAt`. Browser filtering is client-side over the
 * loaded window (parity with the conversation list).
 */
export function useCommsWebVisits(
  caseId: string,
  extractionId: string | null,
  range: CommsRange = {}
) {
  return useInfiniteQuery({
    queryKey: commsKeys.webVisits(caseId, extractionId ?? '', range),
    queryFn: ({ pageParam }) =>
      commsApi.listWebVisits(caseId, extractionId as string, {
        offset: pageParam,
        limit: PAGE_SIZE,
        ...range,
      }),
    initialPageParam: 0,
    getNextPageParam: (last: CommsWebVisitsPage, allPages) =>
      last.hasMore ? allPages.length * PAGE_SIZE : undefined,
    enabled: !!caseId && !!extractionId,
    staleTime: IMMUTABLE,
  })
}

/**
 * Promote a contiguous message range into the graph as one `MessageExcerpt`
 * (ADR-0024). The server validates the range against parquet and assembles the
 * verbatim text — the client only names the boundaries.
 */
export function usePromoteExcerpt(caseId: string, extractionId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CommsExcerptCreate) =>
      commsApi.createExcerpt(caseId, extractionId as string, body),
    // The excerpt list is the one comms query that is NOT immutable — a
    // promotion changes it, so the thread's markers refresh from the server.
    onSuccess: (_excerpt, body) =>
      queryClient.invalidateQueries({
        queryKey: commsKeys.excerpts(caseId, extractionId ?? '', body.conversationId),
      }),
  })
}

/** Messages per bucket for the open thread — the histogram above the reader.
 * Immutable like the rest of the parquet reads. */
export function useCommsActivity(
  caseId: string,
  extractionId: string | null,
  conversationId: string | null,
  bucket: CommsActivityBucketSize = 'day'
) {
  return useQuery({
    queryKey: commsKeys.activity(caseId, extractionId ?? '', conversationId ?? '', bucket),
    queryFn: () =>
      commsApi.conversationActivity(
        caseId,
        extractionId as string,
        conversationId as string,
        bucket
      ),
    enabled: !!caseId && !!extractionId && !!conversationId,
    staleTime: IMMUTABLE,
  })
}

/**
 * Extract entities from a promoted excerpt (`origin: mined`, ADR-0024). A
 * second, explicit act after promotion: it runs the standard schema-driven
 * chain, so what a chat names resolves against what the case already holds.
 */
export function useMineExcerpt(caseId: string, extractionId: string | null) {
  return useMutation({
    mutationFn: (excerptId: string) =>
      commsApi.mineExcerpt(caseId, extractionId as string, excerptId),
  })
}

/** Excerpts already promoted from one thread — gutter markers + the panel. */
export function useCommsExcerpts(
  caseId: string,
  extractionId: string | null,
  conversationId: string | null
) {
  return useQuery({
    queryKey: commsKeys.excerpts(caseId, extractionId ?? '', conversationId ?? ''),
    queryFn: () => commsApi.listExcerpts(caseId, extractionId as string, conversationId as string),
    enabled: !!caseId && !!extractionId && !!conversationId,
  })
}

/**
 * The OS notification record (ADR-0018), newest first, offset-paged like the
 * web history. `registered` and `apps` ride on the first page only.
 */
export function useCommsNotifications(
  caseId: string,
  extractionId: string | null,
  range: CommsRange = {}
) {
  return useInfiniteQuery({
    queryKey: commsKeys.notifications(caseId, extractionId ?? '', range),
    queryFn: ({ pageParam }) =>
      commsApi.listNotifications(caseId, extractionId as string, {
        offset: pageParam,
        limit: PAGE_SIZE,
        ...range,
      }),
    initialPageParam: 0,
    getNextPageParam: (last: CommsNotificationsPage, allPages) =>
      last.hasMore ? allPages.length * PAGE_SIZE : undefined,
    enabled: !!caseId && !!extractionId,
    staleTime: IMMUTABLE,
  })
}
